import { useParams } from 'react-router'
import authStore from '../../../../store/authStore'
import { useCallback, useEffect, useState } from 'react'
import { FetchStatus } from '../../../../types/fetch-status.types'
import constants from '../../../../constants'
import { LeaveRequest } from '../../../../database/tables'
import PERMISSIONS from '../../../../private/permissions'
import { useForm } from 'react-hook-form'
import leaveRequestEditSchema, { LeaveRequestEditSchema } from '../../../../database/validations/LeaveRequestEdit'
import { zodResolver } from '@hookform/resolvers/zod'
import toast from 'react-hot-toast'
import { LeaveRequestSource } from '../../../../database/enums/LeaveRequestSource.enum'
import { AccountTeam } from '../../../../database/enums/AccountTeam.enum'
import { LeaveTime } from '../../../../database/enums/LeaveTime.enum'
import { LeaveType } from '../../../../database/enums/LeaveType.enum'
import LeaveRequestService from '../../../../services/leave-request'
import { ApprovalStatus } from '../../../../database/enums/ApprovalStatus.enum'

const formEmptyValue: LeaveRequestEditSchema = {
  source: LeaveRequestSource.INDO,
  name: '',
  email: '',
  accountTeam: AccountTeam.PRODUCT_AND_MARKETING,
  position: '',
  startDate: new Date(),
  endDate: new Date(),
  leaveTime: LeaveTime.FULL_DAY,
  leaveType: LeaveType.ANNUAL_LEAVE,
  otherLeaveType: '',
  reason: '',
}

export default function useAdminLeaveRequestEditViewModel() {
  const params = useParams()
  const id = params.id as string | null
  const user = authStore(state => state.user)

  const [dataUpdateStatus, setDataUpdateStatus] = useState<FetchStatus>(constants.fetchStatus.default)
  const [dataFetchStatus, setDataFetchStatus] = useState<FetchStatus>(constants.fetchStatus.default)
  const [detailData, setDetailData] = useState<LeaveRequest | null>(null)

  const canEdit = useCallback(() => {
    if (!!user && !!user.email) return PERMISSIONS.getEditReimbursementEmails().includes(user.email)
    return false
  }, [user])()

  const formHook = useForm<LeaveRequestEditSchema>({
    defaultValues: formEmptyValue,
    mode: 'onChange',
    resolver: zodResolver(leaveRequestEditSchema),
  })

  async function fetchDetailData() {
    if (!!id) {
      setDataFetchStatus({ ...constants.fetchStatus.default, isLoading: true })
      await LeaveRequestService.getById({ id })
        .then(res => {
          setDataFetchStatus({ ...constants.fetchStatus.default, isSuccess: true })
          setDetailData(res)
          formHook.reset({
            ...formEmptyValue,
            source: res.source,
            name: res.name,
            email: res.email,
            accountTeam: res.accountTeam,
            position: res.position,
            startDate: res.startDate,
            endDate: res.endDate,
            leaveTime: res.leaveTime,
            leaveType: res.leaveType,
            otherLeaveType: res.otherLeaveType,
            reason: res.reason,
          })
        })
        .catch(error => {
          console.log(error)
          setDataFetchStatus({ ...constants.fetchStatus.default, isError: true })
        })
    }
  }

  async function onSubmit(data: LeaveRequestEditSchema) {
    if (canEdit) {
      // TODO: remove comment if edit restriction is needed
      // if (detailData?.approvalStatus === ApprovalStatus.UNRESPONDED) {
      if (!!id) {
        setDataUpdateStatus({ ...constants.fetchStatus.default, isLoading: true })
        await LeaveRequestService.update({
          id,
          data: {
            source: data.source as LeaveRequestSource,
            name: data.name,
            email: data.email,
            accountTeam: data.accountTeam as AccountTeam,
            position: data.position,
            startDate: data.startDate,
            endDate: data.endDate,
            leaveTime: data.leaveTime as LeaveTime,
            leaveType: data.leaveType as LeaveType,
            otherLeaveType: data.otherLeaveType ?? undefined,
            reason: data.reason ?? undefined,
          },
          updatedBy: user?.displayName ?? user?.email ?? user?.uid ?? null,
        })
          .then(() => {
            setDataUpdateStatus({ ...constants.fetchStatus.default, isSuccess: true })
            toast.success('Update succeed!')
            fetchDetailData()
          })
          .catch(error => {
            console.log(error)
            setDataUpdateStatus({ ...constants.fetchStatus.default, isError: true })
            toast.error('Update failed!')
          })
      }
      // } else toast.error('This document is not editable anymore!')
    } else toast.error("You don't have permission to do this action!")
  }

  useEffect(() => {
    fetchDetailData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  function resetPage() {
    if (detailData) {
      formHook.reset({
        ...formEmptyValue,
        source: detailData.source,
        name: detailData.name,
        email: detailData.email,
        accountTeam: detailData.accountTeam,
        position: detailData.position,
        startDate: detailData.startDate,
        endDate: detailData.endDate,
        leaveTime: detailData.leaveTime,
        leaveType: detailData.leaveType,
        otherLeaveType: detailData.otherLeaveType,
        reason: detailData.reason,
      })
      setDataUpdateStatus(constants.fetchStatus.default)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return {
    id,
    detailData,
    dataFetchStatus,
    dataUpdateStatus,
    onSubmit,
    resetPage,
    formHook,
    canEdit,
  }
}
